import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createPortal } from 'react-dom';
import { useTranslator } from '@/hooks/useTranslator';
import { useIntercomLauncherDismiss } from './hooks/useIntercomLauncherDismiss';

/**
 * Small ✕ sitting just above the Intercom bubble, so the user can put the
 * launcher away when it covers what is underneath it.
 *
 * Positioned to match Intercom's own launcher offsets (20px from each edge,
 * 48px bubble), which are fixed by the widget and not configurable from here.
 *
 * Portalled to `<body>`, which is the whole point of the wrapper: `__root`
 * puts the app inside a `position: fixed` shell, and a fixed element *always*
 * creates a stacking context — so rendered in place, this button's z-index is
 * scoped to that shell, which sits at `z-index: auto` in the root context. A
 * sheet portals straight to `<body>` at `z-40` and therefore painted over it,
 * however large the number here got. Intercom's own launcher escapes this by
 * injecting its iframe into `<body>` directly; the ✕ has to do the same to sit
 * beside it.
 */
export function IntercomLauncherDismiss() {
  const { getTranslation: t } = useTranslator({ page: 'shared' });
  const { visible, dismiss } = useIntercomLauncherDismiss();

  if (!visible) return null;

  return createPortal(
    <button
      type='button'
      onClick={dismiss}
      aria-label={t('intercomLauncher.dismiss')}
      title={t('intercomLauncher.dismiss')}
      // Sidebar navy rather than a pale outline: this sits on whatever the
      // page happens to show through beside the bubble, and a muted chip was
      // easy to miss there.
      //
      // One step above `--z-intercom`, so it rides with the bubble instead of
      // tracking a number Intercom owns — see the overlay stacking block in
      // globals.css.
      className='fixed bottom-[72px] right-5 z-[calc(var(--z-intercom)+1)] flex size-5 items-center justify-center rounded-full bg-sidebar text-sidebar-foreground shadow-sm transition-colors hover:bg-sidebar/90'
    >
      <FontAwesomeIcon icon={faXmark} className='size-3' />
    </button>,
    document.body,
  );
}
